import { serveFpsMultiSiteSearchExperiment } from '@atlassian/search-common/experiments';

import { type CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import type {
	CreatedSelectFilter,
	SelectFilterMonad,
} from '../../../../../common/constants/filters/select-filter/types';
import { getSiteCloudId } from '../../../../../common/constants/filters/site';
import { all1pProductKeys, all3pProductKeys } from '../../../../../common/constants/product-keys';
import { getDefaultSiteIds } from '../../../../../common/utils/sites/default-sites';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';

const baseFilter = createMultiSelectFilter<'site'>({
	id: 'site',
	products: [...all1pProductKeys, ...all3pProductKeys],
	alwaysVisible: false,
	universal: true,
	queryParams: {
		key: 'sites',
		fetcher: async (queryParams, config) => {
			return (config.unitMetadata ?? [])
				.map((site) => ({ cloudId: getSiteCloudId(site.id), siteName: site.siteName }))
				.filter(
					({ cloudId }) =>
						cloudId !== config.cloudId && // Excludes the current site (injected as display-only)
						queryParams.includes(cloudId),
				)
				.map(({ cloudId, siteName }) => ({
					trackingKey: cloudId,
					value: cloudId,
					label: siteName,
					queryParamValue: cloudId,
					isSelected: true,
				}));
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			return (config.unitMetadata ?? [])
				.map((site) => ({ cloudId: getSiteCloudId(site.id), siteName: site.siteName }))
				.filter(
					({ cloudId }) => cloudId !== config.cloudId, // Excludes the current site (injected as display-only)
				)
				.map(({ cloudId, siteName }) => ({
					trackingKey: cloudId,
					value: cloudId,
					label: siteName,
					queryParamValue: cloudId,
				}));
		},
	},
});

// Keep the default scope out of the URL; a primary-only param distinguishes opting out
// from the absent param that restores the default selection.
const withDefaultSites = (
	monad: SelectFilterMonad<'site'>,
	config: CloudConfig,
): SelectFilterMonad<'site'> => {
	if (
		serveFpsMultiSiteSearchExperiment({
			primaryProduct: config.primaryProduct,
			fireExperimentExposure: false,
		}) !== 'test-2'
	) {
		return monad;
	}
	const defaultIds = getDefaultSiteIds(config.unitMetadata, config.cloudId);
	const defaultAdditionalIds = defaultIds.filter((id) => id !== config.cloudId);
	const wrap = (next: SelectFilterMonad<'site'>) => withDefaultSites(next, config);

	return {
		...monad,
		queryParams: {
			...monad.queryParams,
			get: () => {
				const ids = monad.getValue();
				const isDefault =
					ids.length === defaultAdditionalIds.length &&
					defaultAdditionalIds.every((id) => ids.includes(id));
				return {
					sites: isDefault ? [] : ids.length ? ids : config.cloudId ? [config.cloudId] : [],
				};
			},
			set: async (params, onStateChange) =>
				wrap(
					await monad.queryParams.set(
						{ ...params, sites: params.sites ?? defaultIds },
						onStateChange,
					),
				),
		},
		setOption: (...args) => wrap(monad.setOption(...args)),
		loadInitialOptions: async (...args) => wrap(await monad.loadInitialOptions(...args)),
		setLookupInput: async (...args) => wrap(await monad.setLookupInput(...args)),
		clearOptions: () => wrap(monad.clearOptions()),
		clear: () => wrap(monad.clear()),
	};
};

export const filter: CreatedSelectFilter<'site'> = {
	...baseFilter,
	getInitialState: (config: CloudConfig) =>
		withDefaultSites(baseFilter.getInitialState(config), config),
	withState: (state: unknown, config: CloudConfig) =>
		withDefaultSites(baseFilter.withState(state, config), config),
};
